/* eslint-disable react/no-did-update-set-state */

import React from 'react';
import PropTypes from 'prop-types';
import { isNil, isNaN } from 'lodash';

import TextFieldStateless from './TextFieldStateless';
import announce from '../../../helpers/announce';
import formatNumber from '../../../helpers/formatNumber';

const PARTIAL_NUMBER_PATTERN = /^[-]?[0-9]?([0-9,]+)?([.][0-9]{0,2})?$/;

export default class DollarField extends React.PureComponent {
    static propTypes = {
        /** Label contents, usually text */
        label: PropTypes.node.isRequired,

        /** html id required to connect the input with the label */
        id: PropTypes.string.isRequired,

        /** Input name */
        name: PropTypes.string,

        /** If false, the label will be screen reader only */
        isLabelVisible: PropTypes.bool,

        /** html input type, i.e. "text" */
        type: PropTypes.oneOf(['text', 'password', 'search', 'email', 'tel']),

        /** Text to be used for the input placeholder */
        placeholder: PropTypes.string,

        /** The input's value. Use only when the input is controlled */
        value: PropTypes.number,

        /** Initial value the field should have on mount */
        defaultValue: PropTypes.number,

        /** Disables the field */
        isDisabled: PropTypes.bool,

        /** Marks the field as required; Adds required styling to label */
        isRequired: PropTypes.bool,

        /** Makes the label and input arranged inline horizontally */
        isInline: PropTypes.bool,

        /** Ref to be passed to the input */
        innerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.node]),

        /** Show clear button if true and there is a value */
        hasClearButton: PropTypes.bool,

        /** If true, the input will gain focus when it mounts */
        shouldFocusOnMount: PropTypes.bool,

        /** Additional className added to outer container for custom styling */
        className: PropTypes.string,

        /** Maximum number of characters allowed in the input */
        maxLength: PropTypes.string,

        /** Function fired when the field changes */
        onChange: PropTypes.func,

        /** Function fired when the field is blurred */
        onBlur: PropTypes.func,

        /** Minimum amount for field value to be valid */
        min: PropTypes.number,

        /** Maximum amount for field value to be valid */
        max: PropTypes.number,

        /** Performs the validation as soon as the component mounts */
        shouldValidateOnMount: PropTypes.bool,

        /** Enables validation as you type */
        shouldValidateOnUpdate: PropTypes.bool,

        /** Callback invoked when isInvalid/errorText changes state. Returns an
        object with the id, isValid, and errorText */
        handleNewErrorState: PropTypes.func,

        /** Manually marks and styles the field as invalid */
        isInvalid: PropTypes.bool,

        /** Manually set contents of the error message, usually text */
        errorText: PropTypes.node,

        /** Adds a unit (such as '$') that appears to the left of the input */
        unitBefore: PropTypes.string,

        /** If using unitBefore or unitAfter, you also need to supply a description
         * of that unit for screen readers. For example, if you use unitBefore="$",
         * you should also have srUnitDescription="dollars".
         */
        srUnitDescription: PropTypes.string
    };

    static defaultProps = {
        unitBefore: '$',
        maxLength: "20",
        isDisabled: false,
        isLabelVisible: true,
        value: undefined,
        type: 'text',
        placeholder: undefined,
        isRequired: false,
        isInvalid: false,
        isInline: false,
        errorText: undefined,
        innerRef: undefined,
        hasClearButton: false,
        className: undefined,
        defaultValue: undefined,
        onChange: undefined,
        onBlur: undefined,
        shouldFocusOnMount: false,
        shouldValidateOnMount: false,
        shouldValidateOnUpdate: false,
        handleNewErrorState: undefined,
        srUnitDescription: 'dollars'
    };

    constructor(props) {
        super(props);

        const hasErrorText = typeof props.errorText === 'string' && props.errorText.length > 0;

        let initialValue = '';

        if (!isNil(props.value)) {
            initialValue = formatNumber(props.value);
        }
        else if (!isNil(props.defaultValue)) {
            initialValue = formatNumber(props.defaultValue);
        }

        this.state = {
            value: initialValue,
            isPristine: !(props.value || props.defaultValue),
            isInvalid: props.isInvalid || hasErrorText || false,
            errorText: props.errorText || undefined
        };

        this.errorState = {
            isInvalid: props.isInvalid,
            errorText: props.errorText
        };
    }

    componentDidMount() {
        const { shouldValidateOnMount } = this.props;

        if (shouldValidateOnMount) {
            clearTimeout(this.timeout);
            this.timeout = setTimeout(() => {
                this.validate();
            }, 0);
        }
    }

    componentDidUpdate(prevProps) {
        const { shouldValidateOnUpdate, isInvalid, errorText, value } = this.props;

        if (shouldValidateOnUpdate) {
            this.validate();
        }
        else if (isInvalid !== prevProps.isInvalid || errorText !== prevProps.errorText) {
            const hasErrorText = typeof errorText === 'string' && errorText.length > 0;

            this.setState({
                isInvalid: isInvalid || hasErrorText,
                errorText
            });
        }

        // Sync change in props and state - DollarField can not be a true
        // controlled component because of the way we're formatting the dollar
        // value on blur ... props is a number and state is a string
        if (!isNil(value) && this.isControlled() && value !== parseFloat(this.state.value.replace(/,/g, ''))) {
            this.setState({
                value: formatNumber(value)
            });

            if (this.state.isInvalid) {
                this.validate();
            }
        }
    }

    componentWillUnmount() {
        // Safety in case of render thrashing
        clearTimeout(this.timeout);
    }

    getValue = () => {
        if (this.isControlled()) {
            return this.props.value;
        }

        const value = parseFloat(this.state.value.replace(/,/g, ''), 10);
        return isNaN(value) ? null : value;
    }

    isControlled() {
        const { onChange } = this.props;
        if (!isNil(onChange)) {
            return true;
        }
        return false;
    }

    clearError = () => {
        this.setState({
            isInvalid: false,
            errorText: undefined
        });
    }

    handleNewErrorState = ({ isInvalid, errorText }) => {
        const { handleNewErrorState, id, name } = this.props;

        // bail if no handleNewErrorState function exists
        if (!handleNewErrorState) {
            return;
        }

        // compare new state to old state. If different, invoke callback
        if (isInvalid !== this.errorState.isInvalid || errorText !== this.errorState.errorText) {
            handleNewErrorState({
                id,
                name,
                isInvalid,
                errorText
            });

            this.errorState = {
                isInvalid,
                errorText
            };
        }
    }

    handleClearButtonClick = (event) => {
        event.preventDefault();

        const { onChange } = this.props;

        this.setState({
            value: '',
            isPristine: true,
            isInvalid: false,
            errorText: undefined
        });

        if (onChange) {
            onChange(null);
        }

        this.handleNewErrorState({
            isValid: false,
            errorText: undefined
        });
    }

    handleChange = (event) => {
        event.preventDefault();

        const value = event.target.value;
        const isValidPartialNumber = PARTIAL_NUMBER_PATTERN.test(value);

        if (isValidPartialNumber) {
            const valueFloat = value === '' || value === '-'
                ? null
                : parseFloat(value.replace(/,/g, ''), 10);
            const { onChange } = this.props;

            this.setState({
                value,
                isPristine: false
            });

            if (onChange) {
                onChange(valueFloat);
            }
        }
        else if (value === '') {
            this.setState({
                value: ''
            });
        }
        else {
            announce('Error: Enter only numbers with a maximum of 2 decimal places.');
        }
    };

    handleBlur = (event) => {
        const { isRequired, onBlur, onChange, min, max } = this.props;
        const { isPristine, isInvalid, errorText } = this.state;
        const isControlled = this.isControlled();

        const unformattedValue = isControlled
            ? this.props.value
            : parseFloat(this.state.value.replace(/,/g, ''), 10);

        const value = formatNumber(unformattedValue);

        this.setState({
            value
        });

        if (isControlled && onChange) {
            const valueFloat = value === '' ? null : parseFloat(value.replace(/,/g, ''), 10);
            onChange(valueFloat);
        }

        if ((isRequired || !isNil(max) || !isNil(min)) && !isPristine) {
            // this.validate() performs the validation and handles errors, but we're
            // also returning a copy of the state for use in onBlur. This is to avoid
            // more complex setState callbacks and having to call onBlur inside
            // this.validate()
            const validationState = this.validate();

            if (onBlur) {
                onBlur(event, { value, ...validationState });
            }

            if (validationState.isInvalid && validationState.errorText) {
                announce(validationState.errorText, 'assertive');
            }
        }
        else if (onBlur) {
            onBlur(event, { value, isInvalid, errorText });
        }
    };

    validate = () => {
        const { isRequired, id, min, max } = this.props;
        const value = this.isControlled()
            ? this.props.value
            : parseFloat(this.state.value.replace(/,/g, ''), 10);

        const validationState = {
            isInvalid: false,
            errorText: null,
            id
        };

        // When the field is empty
        if (isNil(value)) {
            if (isRequired) {
                const errorText = typeof this.props.label === 'string'
                    ? `${this.props.label} is required.`
                    : 'This field is required';
                validationState.isInvalid = true;
                validationState.errorText = errorText;
            }
            else {
                // Reset error state
                this.clearError();
            }
        }

        // When the field has a value and there's a min/max
        if (!isNil(value) && (!isNil(min) || !isNil(max))) {
            if (!isNil(max) && value > max) {
                validationState.isInvalid = true;
                validationState.errorText = `${this.props.label} must be $${formatNumber(max)} or less.`;
            }
            else if (!isNil(min) && value < min) {
                validationState.isInvalid = true;
                validationState.errorText = `${this.props.label} must be $${formatNumber(min)} or more.`;
            }
        }

        // Update local component state
        this.setState(validationState);
        this.handleNewErrorState(validationState);

        // Return values to be passed on to onBlur
        return validationState;
    };

    render() {
        const {
            defaultValue,
            shouldValidateOnMount,
            shouldValidateOnUpdate,
            handleNewErrorState,
            maxLength,
            ...rest
        } = this.props;

        const { isInvalid, errorText, value } = this.state;

        return (
            <TextFieldStateless
                {...rest}
                maxLength={maxLength}
                onChange={this.handleChange}
                onBlur={this.handleBlur}
                handleClearButtonClick={this.handleClearButtonClick}
                isInvalid={isInvalid}
                errorText={errorText}
                value={value} />
        );
    }
}
